import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { reportesService, type MetricaReporte as MetricaReporteAPI, type DatosGrafico as DatosGraficoAPI, type ReportePersonalizado as ReportePersonalizadoAPI, type FiltrosReporte as FiltrosReporteAPI } from '../../../services'
import { ChartBarIcon, ChartPieIcon, DocumentChartBarIcon, ArrowTrendingUpIcon, ArrowTrendingDownIcon, FunnelIcon, ArrowDownTrayIcon, PlayIcon, TrashIcon } from '@heroicons/react/24/outline'
import { usePermisos } from '../../../ganchos/usePermisos'
import { resolverPresentacionErrorApi } from '../../../services/apiError'

// Interfaces extendidas del servicio
interface MetricaReporte extends MetricaReporteAPI {
  icono: JSX.Element
  color: string
}

interface DatosGrafico extends DatosGraficoAPI {}

interface ReportePersonalizado extends ReportePersonalizadoAPI {}

interface FiltrosReporte extends FiltrosReporteAPI {
  granularidad: 'dia' | 'semana' | 'mes' | 'año'
}

type FormularioReporte = {
  nombre: string
  descripcion: string
  tipo: ReportePersonalizado['tipo']
  frecuencia: NonNullable<ReportePersonalizado['frecuencia']>
}

const PaginaReportesAnaliticas: React.FC = () => {
  const [metricas, setMetricas] = useState<MetricaReporte[]>([])
  const [datosGraficos, setDatosGraficos] = useState<DatosGrafico[]>([])
  const [reportesPersonalizados, setReportesPersonalizados] = useState<ReportePersonalizado[]>([])
  const [cargando, setCargando] = useState(true)
  const [filtros, setFiltros] = useState<FiltrosReporte>({
    fechaInicio: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    fechaFin: new Date().toISOString().split('T')[0],
    tipoReporte: '',
    categoria: '',
    usuario: '',
    granularidad: 'dia'
  })
  const [vistaActual, setVistaActual] = useState<'dashboard' | 'reportes' | 'personalizado'>('dashboard')
  const [mostrarFiltros, setMostrarFiltros] = useState(false)
  const [graficoSeleccionado, setGraficoSeleccionado] = useState<'lineas' | 'barras' | 'pie' | 'area'>('lineas')
  const [guardandoReporte, setGuardandoReporte] = useState(false)
  const [mensajeAccion, setMensajeAccion] = useState<string | null>(null)
  const [errorAccion, setErrorAccion] = useState<string | null>(null)
  const [nuevoReporte, setNuevoReporte] = useState<FormularioReporte>({
    nombre: '',
    descripcion: '',
    tipo: 'actividad',
    frecuencia: 'manual'
  })
  const { tienePermiso } = usePermisos()

  const cargarDatos = async () => {
    setCargando(true)
    try {
      const metricasData = await reportesService.obtenerMetricas(filtros)
      const metricasConIconos = metricasData.map(metrica => ({
        ...metrica,
        icono: <ChartBarIcon className="h-6 w-6" />,
        color: '#3B82F6'
      }))
      setMetricas(metricasConIconos)

      const datosGraficosData = await reportesService.obtenerDatosGrafico('actividad', filtros)
      setDatosGraficos(datosGraficosData)

      const reportesData = await reportesService.obtenerReportesPersonalizados()
      setReportesPersonalizados(reportesData)
    } catch (error) {
      console.error('Error al cargar datos de reportes:', error)
      setMetricas([])
      setDatosGraficos([])
      setReportesPersonalizados([])
      const presentacion = resolverPresentacionErrorApi(error, {
        tituloFallback: 'Error al cargar reportes',
        mensajeFallback: 'No se pudieron obtener los reportes personalizados y métricas del módulo.',
      })
      setErrorAccion(presentacion.message)
    } finally {
      setCargando(false)
    }
  }

  useEffect(() => {
    cargarDatos()
  }, [filtros])

  const calcularPorcentajeCambio = (valorActual: number, valorAnterior: number): number => {
    if (valorAnterior === 0) return 0
    return ((valorActual - valorAnterior) / valorAnterior) * 100
  }

  const formatearValor = (valor: number, tipo: string, unidad: string): string => {
    switch (tipo) {
      case 'porcentaje':
        return `${valor.toFixed(1)}%`
      case 'tiempo':
        return `${valor.toFixed(1)} ${unidad}`
      case 'bytes':
        return `${valor.toFixed(1)} ${unidad}`
      default:
        return `${valor.toLocaleString()} ${unidad}`
    }
  }

  const obtenerColorEstadoReporte = (estado: string) => {
    switch (estado) {
      case 'activo':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      case 'pausado':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
      case 'error':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
    }
  }

  const obtenerColorTipoReporte = (tipo: string) => {
    switch (tipo) {
      case 'usuarios':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
      case 'archivos':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      case 'actividad':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
      case 'rendimiento':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
      case 'financiero':
        return 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
    }
  }

  const exportarReporte = async (formato: 'pdf' | 'excel' | 'csv') => {
    try {
      setCargando(true)
      const blob = await reportesService.exportarReporte(formato, filtros)
      
      // Crear URL para descargar el archivo
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `reporte_${new Date().toISOString().split('T')[0]}.${formato}`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error(`Error al exportar reporte en formato ${formato}:`, error)
    } finally {
      setCargando(false)
    }
  }

  const handleCrearReporte = async (e: React.FormEvent) => {
    e.preventDefault()
    setGuardandoReporte(true)
    setMensajeAccion(null)
    setErrorAccion(null)
    try {
      await reportesService.crearReporte({
        ...nuevoReporte,
        estado: 'activo',
        parametros: {
          fechaInicio: filtros.fechaInicio,
          fechaFin: filtros.fechaFin,
          granularidad: filtros.granularidad,
        },
      })
      setNuevoReporte({
        nombre: '',
        descripcion: '',
        tipo: 'actividad',
        frecuencia: 'manual'
      })
      setVistaActual('reportes')
      setMensajeAccion('Reporte personalizado creado correctamente.')
      await cargarDatos()
    } catch (error) {
      const presentacion = resolverPresentacionErrorApi(error, {
        tituloFallback: 'Error al crear reporte',
        mensajeFallback: 'No se pudo guardar el reporte personalizado.',
      })
      setErrorAccion(presentacion.message)
    } finally {
      setGuardandoReporte(false)
    }
  }

  const handleEjecutarReporte = async (id: string) => {
    setMensajeAccion(null)
    setErrorAccion(null)
    try {
      await reportesService.ejecutarReporte(id)
      setMensajeAccion('Reporte ejecutado correctamente.')
      await cargarDatos()
    } catch (error) {
      const presentacion = resolverPresentacionErrorApi(error, {
        tituloFallback: 'Error al ejecutar reporte',
        mensajeFallback: 'No se pudo ejecutar el reporte personalizado.',
      })
      setErrorAccion(presentacion.message)
    }
  }

  const handleEliminarReporte = async (id: string) => {
    if (!window.confirm('¿Deseas eliminar este reporte personalizado?')) {
      return
    }

    setMensajeAccion(null)
    setErrorAccion(null)
    try {
      await reportesService.eliminarReporte(id)
      setMensajeAccion('Reporte eliminado correctamente.')
      await cargarDatos()
    } catch (error) {
      const presentacion = resolverPresentacionErrorApi(error, {
        tituloFallback: 'Error al eliminar reporte',
        mensajeFallback: 'No se pudo eliminar el reporte personalizado.',
      })
      setErrorAccion(presentacion.message)
    }
  }

  const generarGraficoLineas = () => {
    if (datosGraficos.length === 0) {
      return (
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Tendencias de Actividad</h3>
          <div className="h-64 flex items-center justify-center rounded bg-gray-50 text-sm text-gray-500 dark:bg-gray-700 dark:text-gray-400">
            No hay datos suficientes para graficar todavía.
          </div>
        </div>
      )
    }

    const maxValor = Math.max(1, ...datosGraficos.map(d => Math.max(d.usuarios, d.descargas, d.uploads)))
    const altura = 200
    const ancho = 600
    
    const puntos = datosGraficos.map((dato, index) => {
      const x = (index / (datosGraficos.length - 1)) * ancho
      const y = altura - (dato.usuarios / maxValor) * altura
      return `${x},${y}`
    }).join(' ')

    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Tendencias de Actividad</h3>
          <div className="flex space-x-2">
            <button
              onClick={() => setGraficoSeleccionado('lineas')}
              className={`px-3 py-1 rounded text-sm ${
                graficoSeleccionado === 'lineas'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
              }`}
            >
              Líneas
            </button>
            <button
              onClick={() => setGraficoSeleccionado('barras')}
              className={`px-3 py-1 rounded text-sm ${
                graficoSeleccionado === 'barras'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
              }`}
            >
              Barras
            </button>
          </div>
        </div>
        <div className="h-64 flex items-center justify-center bg-gray-50 dark:bg-gray-700 rounded">
          <svg width={ancho} height={altura} className="overflow-visible">
            <polyline
              points={puntos}
              fill="none"
              stroke="#3B82F6"
              strokeWidth="2"
              className="drop-shadow-sm"
            />
            {datosGraficos.map((dato, index) => {
              const x = (index / (datosGraficos.length - 1)) * ancho
              const y = altura - (dato.usuarios / maxValor) * altura
              return (
                <circle
                  key={index}
                  cx={x}
                  cy={y}
                  r="4"
                  fill="#3B82F6"
                  className="hover:r-6 transition-all cursor-pointer"
                />
              )
            })}
          </svg>
        </div>
        <div className="mt-4 flex justify-center space-x-6 text-sm">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
            <span className="text-gray-600 dark:text-gray-400">Usuarios</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-green-600 rounded-full"></div>
            <span className="text-gray-600 dark:text-gray-400">Descargas</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-purple-600 rounded-full"></div>
            <span className="text-gray-600 dark:text-gray-400">Uploads</span>
          </div>
        </div>
      </div>
    )
  }

  if (cargando) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Cargando reportes y analíticas...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Reportes y Analíticas</h1>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                Dashboard avanzado con métricas, gráficos y reportes personalizados
              </p>
            </div>
            <div className="mt-4 md:mt-0 flex space-x-3">
              <button
                onClick={() => setMostrarFiltros(!mostrarFiltros)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-200"
              >
                <FunnelIcon className="h-4 w-4 mr-2" />
                Filtros
              </button>
              <div className="flex border border-gray-300 dark:border-gray-600 rounded-lg">
                <button
                  onClick={() => setVistaActual('dashboard')}
                  className={`px-4 py-2 text-sm font-medium rounded-l-lg transition-colors duration-200 ${
                    vistaActual === 'dashboard'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                  }`}
                >
                  Dashboard
                </button>
                <button
                  onClick={() => setVistaActual('reportes')}
                  className={`px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                    vistaActual === 'reportes'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                  }`}
                >
                  Reportes
                </button>
                <button
                  onClick={() => setVistaActual('personalizado')}
                  className={`px-4 py-2 text-sm font-medium rounded-r-lg transition-colors duration-200 ${
                    vistaActual === 'personalizado'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                  }`}
                >
                  Personalizado
                </button>
              </div>
              {tienePermiso('reportes:exportar') && (
                <button
                  onClick={() => exportarReporte('pdf')}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors duration-200"
                >
                  <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                  Exportar
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {mensajeAccion && (
          <div className="mb-6 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700 dark:border-green-900/40 dark:bg-green-950/30 dark:text-green-200">
            {mensajeAccion}
          </div>
        )}
        {errorAccion && (
          <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-200">
            {errorAccion}
          </div>
        )}

        {/* Filtros */}
        <AnimatePresence>
          {mostrarFiltros && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Fecha Inicio
                  </label>
                  <input
                    type="date"
                    value={filtros.fechaInicio}
                    onChange={(e) => setFiltros(prev => ({ ...prev, fechaInicio: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Fecha Fin
                  </label>
                  <input
                    type="date"
                    value={filtros.fechaFin}
                    onChange={(e) => setFiltros(prev => ({ ...prev, fechaFin: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Tipo de Reporte
                  </label>
                  <select
                    value={filtros.tipoReporte}
                    onChange={(e) => setFiltros(prev => ({ ...prev, tipoReporte: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Todos los tipos</option>
                    <option value="usuarios">Usuarios</option>
                    <option value="archivos">Archivos</option>
                    <option value="actividad">Actividad</option>
                    <option value="rendimiento">Rendimiento</option>
                    {tienePermiso('financieros:ver') && (
                      <option value="financiero">Financiero</option>
                    )}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Granularidad
                  </label>
                  <select
                    value={filtros.granularidad}
                    onChange={(e) => setFiltros(prev => ({ ...prev, granularidad: e.target.value as any }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="dia">Por día</option>
                    <option value="semana">Por semana</option>
                    <option value="mes">Por mes</option>
                    <option value="año">Por año</option>
                  </select>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Contenido principal */}
        {vistaActual === 'dashboard' && (
          <div className="space-y-8">
            {/* Métricas principales */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {metricas.map((metrica, index) => (
                <motion.div
                  key={metrica.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div
                        className="p-3 rounded-lg"
                        style={{ backgroundColor: metrica.color + '20', color: metrica.color }}
                      >
                        {metrica.icono}
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                          {metrica.nombre}
                        </h3>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">
                          {formatearValor(metrica.valor, metrica.tipo, metrica.unidad)}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`flex items-center space-x-1 ${
                        metrica.tendencia === 'subida' ? 'text-green-600' :
                        metrica.tendencia === 'bajada' ? 'text-red-600' : 'text-gray-600'
                      }`}>
                        {metrica.tendencia === 'subida' && <ArrowTrendingUpIcon className="h-4 w-4" />}
                        {metrica.tendencia === 'bajada' && <ArrowTrendingDownIcon className="h-4 w-4" />}
                        <span className="text-sm font-medium">
                          {Math.abs(calcularPorcentajeCambio(metrica.valor, metrica.valorAnterior)).toFixed(1)}%
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        vs período anterior
                      </p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-3">
                    {metrica.descripcion}
                  </p>
                </motion.div>
              ))}
            </div>

            {/* Gráficos */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {generarGraficoLineas()}
              
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Distribución por Categorías
                </h3>
                <div className="h-64 flex items-center justify-center bg-gray-50 dark:bg-gray-700 rounded">
                  <div className="text-center">
                    <ChartPieIcon className="h-16 w-16 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-500 dark:text-gray-400">Gráfico de pastel interactivo</p>
                    <p className="text-sm text-gray-400 dark:text-gray-500">Se implementará con librería de gráficos</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabla de actividad reciente */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Actividad Reciente
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Fecha
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Usuarios
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Descargas
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Uploads
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Ingresos
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {datosGraficos.slice(-7).map((dato, index) => (
                      <motion.tr
                        key={dato.fecha}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {new Date(dato.fecha).toLocaleDateString('es-ES')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {dato.usuarios.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {dato.descargas.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {dato.uploads.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          ${dato.ingresos.toLocaleString()}
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {vistaActual === 'reportes' && (
          <div className="space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Reportes Personalizados
                </h3>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {reportesPersonalizados.map((reporte, index) => (
                    <motion.div
                      key={reporte.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          {reporte.nombre}
                        </h4>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${obtenerColorEstadoReporte(reporte.estado)}`}>
                          {reporte.estado}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                        {reporte.descripcion}
                      </p>
                      <div className="flex items-center justify-between mb-3">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${obtenerColorTipoReporte(reporte.tipo)}`}>
                          {reporte.tipo}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {reporte.ultimaEjecucion ? 
                            `Última: ${new Date(reporte.ultimaEjecucion).toLocaleDateString('es-ES')}` :
                            'Nunca ejecutado'
                          }
                        </span>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEjecutarReporte(reporte.id)}
                          className="flex-1 px-3 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                        >
                          <PlayIcon className="h-4 w-4 inline mr-1" />
                          Ejecutar
                        </button>
                        <button
                          onClick={() => handleEliminarReporte(reporte.id)}
                          className="px-3 py-2 text-sm border border-red-300 text-red-700 rounded hover:bg-red-50 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-900/20 transition-colors"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </div>
                {reportesPersonalizados.length === 0 && (
                  <div className="rounded-lg border border-dashed border-gray-300 px-4 py-8 text-center text-sm text-gray-500 dark:border-gray-600 dark:text-gray-400">
                    Aún no hay reportes personalizados persistidos en la base de datos.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {vistaActual === 'personalizado' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="max-w-3xl">
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Constructor de Reportes Personalizados
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Crea configuraciones reutilizables para ejecutar reportes administrativos con los filtros actuales.
                </p>
              </div>

              <form onSubmit={handleCrearReporte} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Nombre del reporte
                    </label>
                    <input
                      value={nuevoReporte.nombre}
                      onChange={(e) => setNuevoReporte(prev => ({ ...prev, nombre: e.target.value }))}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      placeholder="Ej. Resumen mensual de usuarios"
                      required
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Descripción
                    </label>
                    <textarea
                      value={nuevoReporte.descripcion}
                      onChange={(e) => setNuevoReporte(prev => ({ ...prev, descripcion: e.target.value }))}
                      rows={3}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      placeholder="Describe el objetivo del reporte."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Tipo
                    </label>
                    <select
                      value={nuevoReporte.tipo}
                      onChange={(e) => setNuevoReporte(prev => ({ ...prev, tipo: e.target.value as ReportePersonalizado['tipo'] }))}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    >
                      <option value="actividad">Actividad</option>
                      <option value="usuarios">Usuarios</option>
                      <option value="archivos">Archivos</option>
                      <option value="rendimiento">Rendimiento</option>
                      {tienePermiso('financieros:ver') && <option value="financiero">Financiero</option>}
                      <option value="sistema">Sistema</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Frecuencia
                    </label>
                    <select
                      value={nuevoReporte.frecuencia}
                      onChange={(e) => setNuevoReporte(prev => ({ ...prev, frecuencia: e.target.value as NonNullable<ReportePersonalizado['frecuencia']> }))}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    >
                      <option value="manual">Manual</option>
                      <option value="diario">Diario</option>
                      <option value="semanal">Semanal</option>
                      <option value="mensual">Mensual</option>
                    </select>
                  </div>
                </div>

                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-300">
                  Este reporte se guardará con los filtros actuales del tablero: rango de fechas y granularidad.
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={guardandoReporte}
                    className="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white rounded-lg font-medium transition-colors"
                  >
                    <DocumentChartBarIcon className="h-5 w-5 mr-2" />
                    {guardandoReporte ? 'Guardando...' : 'Crear Nuevo Reporte'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default PaginaReportesAnaliticas
